import React from "react";
import styled from "styled-components";

/**
 * Section 4. Built on native <details>/<summary> exactly as the reference does,
 * so it expands without JavaScript and stays keyboard- and screen-reader
 * accessible for free. The "+" rotates 45deg into an "x" when open.
 *
 * See Hero.styles.ts for the px-not-rem note.
 */
const Wrapper = styled.section`
  background: #fff;

  .inner {
    margin: 0 auto;
    max-width: 896px;
    padding: 80px 20px;
  }

  .head {
    margin-bottom: 40px;
    text-align: center;
  }

  .eyebrow {
    margin: 0 0 12px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    color: var(--brand-red);
  }

  .heading {
    margin: 0;
    font-family: var(--brand-display);
    font-size: 36px;
    font-weight: 800;
    letter-spacing: -0.025em;
    line-height: 1.1;
    color: var(--brand-ink);
  }

  .list {
    border-radius: 24px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: var(--brand-bone);
    overflow: hidden;
  }

  .item + .item {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }

  .item {
    padding: 24px;
  }

  summary {
    display: flex;
    cursor: pointer;
    list-style: none;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  /* hide the default disclosure triangle */
  summary::-webkit-details-marker {
    display: none;
  }
  summary::marker {
    content: "";
  }

  .question {
    font-family: var(--brand-display);
    font-size: 18px;
    font-weight: 800;
    color: var(--brand-ink);
  }

  .toggle {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    background: rgba(14, 17, 22, 0.05);
    color: var(--brand-ink);
    transition: transform 0.25s;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  details[open] .toggle {
    transform: rotate(45deg);
  }

  .answer {
    margin: 12px 0 0;
    font-size: 16px;
    line-height: 1.625;
    color: rgba(14, 17, 22, 0.7);
  }

  @media screen and (min-width: 640px) {
    .inner {
      padding: 80px 32px;
    }
    .heading {
      font-size: 48px;
    }
  }

  @media screen and (min-width: 1024px) {
    .inner {
      padding: 96px 32px;
    }
  }
`;

const Plus = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export type FaqItem = { q: string; a: string };

const Faq = ({ items }: { items: FaqItem[] }) => (
  <Wrapper className="section-lp3-faq">
    <div className="inner">
      <div className="head">
        <p className="eyebrow">Good questions</p>
        <h2 className="heading">About your letter.</h2>
      </div>

      <div className="list">
        {items.map(({ q, a }) => (
          <details className="item" key={q}>
            <summary>
              <span className="question">{q}</span>
              <span className="toggle">
                <Plus />
              </span>
            </summary>
            <p className="answer">{a}</p>
          </details>
        ))}
      </div>
    </div>
  </Wrapper>
);

export default Faq;
